import type { HTMLAttributes, ReactNode, Ref } from "react";

import { Box, Flex, Text } from "~/ui";

import { BLOCK, badgeStyle } from "./action-block-tokens";

export interface ActionBlockBadge {
  label: string;
  tone: "ready" | "saved" | "warn" | "quiet" | "elevated";
}

/** Title, status badges, one summary line, and an aside (batch rule, a link, or a close control). */
export const ActionBlockHeader = ({
  title,
  titleId,
  badges,
  summary,
  aside,
  density = "card",
  headingRef,
}: {
  title: string;
  titleId?: string;
  badges: ActionBlockBadge[];
  summary?: string | null;
  aside?: ReactNode;
  density?: "card" | "pane";
  headingRef?: Ref<HTMLHeadingElement>;
}) => (
  <Flex
    align="flex-start"
    justify="space-between"
    gap="md"
    style={{ padding: density === "pane" ? "16px 20px 12px" : "14px 18px 12px", borderBottom: `1px solid ${BLOCK.divider}` }}
  >
    <Flex direction="column" gap={3} style={{ minWidth: 0 }}>
      <Flex align="center" gap={10} wrap="wrap">
        <Text
          ref={headingRef}
          component="h2"
          id={titleId}
          tabIndex={headingRef ? -1 : undefined}
          style={{ fontSize: density === "pane" ? 16 : BLOCK.title, fontWeight: 600, color: BLOCK.ink, margin: 0, lineHeight: 1.3 }}
        >
          {title}
        </Text>
        {badges.map(badge => (
          <span key={badge.label} role="status" style={badgeStyle(badge.tone)}>
            {badge.label}
          </span>
        ))}
      </Flex>
      {summary && <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{summary}</Text>}
    </Flex>
    {aside && <Box style={{ flex: "none", fontSize: BLOCK.meta, color: BLOCK.secondary, textAlign: "right", paddingTop: 2 }}>{aside}</Box>}
  </Flex>
);

/** Actions on the left, one quiet note on the right, on the block's footer ground. */
export const ActionBlockFooter = ({
  children,
  note,
  density = "card",
}: {
  children?: ReactNode;
  note?: ReactNode;
  density?: "card" | "pane";
}) => (
  <Flex
    align="center"
    gap={10}
    wrap="wrap"
    style={{
      padding: density === "pane" ? "12px 20px 14px" : "12px 18px 14px",
      borderTop: `1px solid ${BLOCK.border}`,
      background: BLOCK.footer,
      borderRadius: `0 0 ${BLOCK.radius}px ${BLOCK.radius}px`,
    }}
  >
    {children}
    {note && <Text style={{ marginLeft: "auto", fontSize: BLOCK.meta, color: BLOCK.secondary, textAlign: "right" }}>{note}</Text>}
  </Flex>
);

/** The card frame: border, 12px radius, hairline shadow. */
export const ActionBlockFrame = ({ children, ...rest }: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) => (
  <Box
    {...rest}
    style={{
      border: `1px solid ${BLOCK.border}`,
      borderRadius: BLOCK.radius,
      background: "#FFFFFF",
      boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      ...rest.style,
    }}
  >
    {children}
  </Box>
);
